// Shape-based native tool result renderers. On shape mismatch, fall back to
// FlattenedJsonResult (better than a raw JSON wall).

import { FlattenedJsonResult } from './FlattenedJsonResult';
import {
  asArray,
  asNumber,
  asString,
  isRecord,
  parseJsonPayload,
} from './parse-tool-payload';
import type { ToolResultRendererProps } from './types';
import {
  Chip,
  ClampedList,
  CodeBlock,
  KeyValueGrid,
  ProseBlock,
} from './shared';

function Fallback({ text, toolName }: ToolResultRendererProps) {
  return <FlattenedJsonResult text={text} toolName={toolName} />;
}

function HitRow({
  title,
  chips,
  summary,
}: {
  title: string;
  chips?: string[];
  summary?: string;
}) {
  return (
    <div className="space-y-0.5 rounded-md py-1">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-medium text-foreground">{title}</span>
        {chips?.filter(Boolean).map((c) => (
          <Chip key={c}>{c}</Chip>
        ))}
      </div>
      {summary && <p className="text-sm text-foreground/80">{summary}</p>}
    </div>
  );
}

export function DocsSearchResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (!isRecord(parsed)) return <Fallback text={text} toolName={toolName} />;
  const hits = asArray(parsed.hits);
  if (!hits) return <Fallback text={text} toolName={toolName} />;

  const rows = hits.map((hit, i) => {
    if (!isRecord(hit)) return null;
    const resource = asString(hit.resource) ?? '';
    const method = asString(hit.method);
    const kind = asString(hit.kind);
    const safety = asString(hit.safety);
    const summary = asString(hit.summary);
    const title = method ? `${resource}.${method}` : resource || `Hit ${i + 1}`;
    return (
      <HitRow
        key={`${title}-${i}`}
        title={title}
        chips={[kind, safety].filter((x): x is string => !!x)}
        summary={summary}
      />
    );
  });

  return (
    <div data-testid="tool-result-docs-search" className="space-y-1">
      {asNumber(parsed.total) != null && (
        <p className="text-xs text-muted-foreground">{asNumber(parsed.total)} results</p>
      )}
      <ClampedList items={rows.filter(Boolean)} />
    </div>
  );
}

export function DocsReadResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (!isRecord(parsed)) return <Fallback text={text} toolName={toolName} />;

  if (parsed.found === false) {
    return (
      <ProseBlock testId="tool-result-docs-read">
        No documentation found for this operation.
      </ProseBlock>
    );
  }

  const resource = asString(parsed.resource) ?? '';
  const method = asString(parsed.method) ?? '';
  const title = method ? `${resource}.${method}` : resource;
  const safety = asString(parsed.safety);
  const description = asString(parsed.description);
  const returns = asString(parsed.returns);

  const argEntries: Array<[string, string]> = [];
  const argSchema = parsed.argSchema;
  if (isRecord(argSchema) && isRecord(argSchema.properties)) {
    const required = new Set(
      Array.isArray(argSchema.required)
        ? argSchema.required.filter((x): x is string => typeof x === 'string')
        : [],
    );
    for (const [name, schema] of Object.entries(argSchema.properties)) {
      const typ = isRecord(schema) ? asString(schema.type) ?? 'any' : 'any';
      const req = required.has(name) ? 'required' : 'optional';
      argEntries.push([name, `${typ} · ${req}`]);
    }
  } else if (isRecord(parsed.params)) {
    for (const [k, v] of Object.entries(parsed.params)) {
      argEntries.push([k, typeof v === 'string' ? v : JSON.stringify(v)]);
    }
  }

  return (
    <div data-testid="tool-result-docs-read" className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-medium">{title || 'Documentation'}</span>
        {safety && <Chip>{safety}</Chip>}
      </div>
      {description && <ProseBlock>{description}</ProseBlock>}
      {argEntries.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">Arguments</div>
          <KeyValueGrid entries={argEntries} />
        </div>
      )}
      {returns && (
        <div>
          <div className="mb-0.5 text-xs font-medium text-muted-foreground">Returns</div>
          <ProseBlock>{returns}</ProseBlock>
        </div>
      )}
    </div>
  );
}

function pickHitTitle(hit: Record<string, unknown>): string {
  return (
    asString(hit.title) ??
    asString(hit.path) ??
    asString(hit.name) ??
    asString(hit.entity_id) ??
    asString(hit.id) ??
    'Result'
  );
}

function pickHitSnippet(hit: Record<string, unknown>): string | undefined {
  return (
    asString(hit.snippet) ??
    asString(hit.preview) ??
    asString(hit.summary) ??
    asString(hit.content)
  );
}

export function SearchHitsResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (!isRecord(parsed) && !Array.isArray(parsed)) {
    // Plain-text search summaries
    if (parsed == null && text.trim()) {
      return <ProseBlock testId="tool-result-search">{text}</ProseBlock>;
    }
    return <Fallback text={text} toolName={toolName} />;
  }

  const hits =
    asArray(isRecord(parsed) ? parsed.hits ?? parsed.results ?? parsed.items : parsed) ?? [];
  if (hits.length === 0 && isRecord(parsed)) {
    return <Fallback text={text} toolName={toolName} />;
  }

  const rows = hits.map((hit, i) => {
    if (!isRecord(hit)) {
      return <ProseBlock key={i}>{String(hit)}</ProseBlock>;
    }
    const entity = asString(hit.entity_type) ?? asString(hit.type);
    return (
      <HitRow
        key={i}
        title={pickHitTitle(hit)}
        chips={entity ? [entity] : undefined}
        summary={pickHitSnippet(hit)}
      />
    );
  });

  return (
    <div data-testid="tool-result-search">
      <ClampedList items={rows} />
    </div>
  );
}

export function GrepResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  // Grep often returns plain text "path:line:content"
  if (parsed == null) {
    const lines = text.split('\n').filter(Boolean);
    if (lines.length === 0) return <ProseBlock testId="tool-result-grep">(no matches)</ProseBlock>;
    return (
      <div data-testid="tool-result-grep">
        <ClampedList
          items={lines.map((line, i) => (
            <CodeBlock key={i} text={line} />
          ))}
        />
      </div>
    );
  }

  const matches = asArray(isRecord(parsed) ? parsed.matches ?? parsed.results ?? parsed.hits : parsed);
  if (!matches) return <Fallback text={text} toolName={toolName} />;

  const rows = matches.map((m, i) => {
    if (!isRecord(m)) return <CodeBlock key={i} text={String(m)} />;
    const path = asString(m.path) ?? asString(m.file) ?? '';
    const line = asNumber(m.line) ?? asNumber(m.line_number);
    const content = asString(m.content) ?? asString(m.text) ?? asString(m.match) ?? '';
    const label = line != null ? `${path}:${line}` : path;
    return (
      <div key={i} className="text-sm">
        <span className="font-medium text-muted-foreground">{label}</span>
        {content && <CodeBlock text={content} />}
      </div>
    );
  });

  return (
    <div data-testid="tool-result-grep">
      <ClampedList items={rows} />
    </div>
  );
}

export function FileContentResult({ text }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (isRecord(parsed)) {
    const content =
      asString(parsed.content) ?? asString(parsed.text) ?? asString(parsed.body) ?? text;
    return <CodeBlock testId="tool-result-file-content" text={content} />;
  }
  return <CodeBlock testId="tool-result-file-content" text={text} />;
}

export function FileListResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (parsed == null) {
    const lines = text.split('\n').filter(Boolean);
    return (
      <div data-testid="tool-result-file-list">
        <ClampedList
          items={lines.map((l, i) => (
            <span key={i} className="text-sm">
              {l}
            </span>
          ))}
        />
      </div>
    );
  }

  const items =
    asArray(isRecord(parsed) ? parsed.entries ?? parsed.files ?? parsed.versions ?? parsed.items : parsed) ??
    [];
  if (items.length === 0) return <Fallback text={text} toolName={toolName} />;

  const rows = items.map((item, i) => {
    if (typeof item === 'string') {
      return (
        <span key={i} className="text-sm">
          {item}
        </span>
      );
    }
    if (!isRecord(item)) return <span key={i}>{String(item)}</span>;
    const name =
      asString(item.name) ??
      asString(item.path) ??
      asString(item.version_id) ??
      asString(item.id) ??
      `Item ${i + 1}`;
    const meta = asString(item.message) ?? asString(item.type) ?? asString(item.kind);
    return (
      <div key={i} className="flex flex-wrap gap-2 text-sm">
        <span className="font-medium">{name}</span>
        {meta && <span className="text-muted-foreground">{meta}</span>}
      </div>
    );
  });

  return (
    <div data-testid="tool-result-file-list">
      <ClampedList items={rows} />
    </div>
  );
}

export function FileMetaResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (!isRecord(parsed)) {
    if (parsed == null && text.trim()) return <ProseBlock testId="tool-result-file-meta">{text}</ProseBlock>;
    return <Fallback text={text} toolName={toolName} />;
  }
  const entries = Object.entries(parsed).map(
    ([k, v]): [string, string] => [k, typeof v === 'string' ? v : JSON.stringify(v)],
  );
  return (
    <div data-testid="tool-result-file-meta">
      <KeyValueGrid entries={entries} />
    </div>
  );
}

export function AckResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (parsed == null) {
    return <ProseBlock testId="tool-result-ack">{text || 'Done.'}</ProseBlock>;
  }
  if (!isRecord(parsed)) {
    return <ProseBlock testId="tool-result-ack">{String(parsed)}</ProseBlock>;
  }

  const summary =
    asString(parsed.message) ??
    asString(parsed.status) ??
    asString(parsed.path) ??
    asString(parsed.version_id) ??
    asString(parsed.id);

  if (summary) {
    return (
      <ProseBlock testId="tool-result-ack">
        {summary}
        {asString(parsed.path) && summary !== asString(parsed.path)
          ? ` · ${asString(parsed.path)}`
          : ''}
      </ProseBlock>
    );
  }

  return <Fallback text={text} toolName={toolName} />;
}

export function WorkItemResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (parsed == null) {
    return <ProseBlock testId="tool-result-work">{text}</ProseBlock>;
  }

  const items = asArray(
    isRecord(parsed)
      ? parsed.teams ??
          parsed.tickets ??
          parsed.columns ??
          parsed.items ??
          parsed.results ??
          (parsed.id || parsed.identifier ? [parsed] : undefined)
      : parsed,
  );

  if (!items || items.length === 0) {
    if (isRecord(parsed)) {
      const name =
        asString(parsed.name) ??
        asString(parsed.title) ??
        asString(parsed.identifier) ??
        asString(parsed.id);
      const state = asString(parsed.state) ?? asString(parsed.status);
      if (name) {
        return (
          <div data-testid="tool-result-work">
            <HitRow title={name} chips={state ? [state] : undefined} summary={asString(parsed.description)} />
          </div>
        );
      }
    }
    return <Fallback text={text} toolName={toolName} />;
  }

  // board_view: columns with tickets
  if (isRecord(parsed) && asArray(parsed.columns)) {
    const cols = asArray(parsed.columns)!;
    const rows = cols.map((col, i) => {
      if (!isRecord(col)) return <span key={i}>{String(col)}</span>;
      const colName = asString(col.name) ?? asString(col.state) ?? `Column ${i + 1}`;
      const tickets = asArray(col.tickets) ?? asArray(col.items) ?? [];
      return (
        <div key={i} className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">
            {colName} ({tickets.length})
          </div>
          {tickets.slice(0, 8).map((t, j) => {
            if (!isRecord(t)) return <div key={j} className="text-sm pl-2">{String(t)}</div>;
            return (
              <div key={j} className="pl-2 text-sm">
                {asString(t.identifier) ?? asString(t.id)}{' '}
                {asString(t.title) ?? asString(t.name) ?? ''}
              </div>
            );
          })}
        </div>
      );
    });
    return (
      <div data-testid="tool-result-work">
        <ClampedList items={rows} />
      </div>
    );
  }

  const rows = items.map((item, i) => {
    if (!isRecord(item)) return <span key={i}>{String(item)}</span>;
    const name =
      asString(item.name) ??
      asString(item.title) ??
      asString(item.identifier) ??
      asString(item.id) ??
      `Item ${i + 1}`;
    const state = asString(item.state) ?? asString(item.status);
    return (
      <HitRow
        key={i}
        title={name}
        chips={state ? [state] : undefined}
        summary={asString(item.description)}
      />
    );
  });

  return (
    <div data-testid="tool-result-work">
      <ClampedList items={rows} />
    </div>
  );
}

export function PinListResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  const pins = asArray(isRecord(parsed) ? parsed.pins ?? parsed.items : parsed);
  if (!pins) {
    if (parsed == null && text.trim()) return <ProseBlock testId="tool-result-pins">{text}</ProseBlock>;
    return <Fallback text={text} toolName={toolName} />;
  }
  const rows = pins.map((pin, i) => {
    if (!isRecord(pin)) return <span key={i}>{String(pin)}</span>;
    const id = asString(pin.item_id) ?? asString(pin.id) ?? `Pin ${i + 1}`;
    const reason = asString(pin.reason);
    const tokens = asNumber(pin.estimated_tokens);
    return (
      <HitRow
        key={i}
        title={id}
        chips={tokens != null ? [`${tokens} tokens`] : undefined}
        summary={reason}
      />
    );
  });
  return (
    <div data-testid="tool-result-pins">
      <ClampedList items={rows} />
    </div>
  );
}

export function ResultSearchResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  const matches = asArray(isRecord(parsed) ? parsed.matches ?? parsed.hits : parsed);
  if (!matches) {
    if (parsed == null && text.trim()) return <ProseBlock testId="tool-result-result-search">{text}</ProseBlock>;
    return <Fallback text={text} toolName={toolName} />;
  }
  const rows = matches.map((m, i) => {
    if (!isRecord(m)) return <CodeBlock key={i} text={String(m)} />;
    const offset = asNumber(m.offset) ?? asNumber(m.start);
    const snippet = asString(m.snippet) ?? asString(m.text) ?? asString(m.match) ?? '';
    return (
      <div key={i} className="text-sm">
        {offset != null && (
          <span className="text-xs text-muted-foreground">@{offset}</span>
        )}
        {snippet && <CodeBlock text={snippet} />}
      </div>
    );
  });
  return (
    <div data-testid="tool-result-result-search">
      <ClampedList items={rows} />
    </div>
  );
}

export function JsonEnvelopeResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (!isRecord(parsed)) {
    return (
      <div
        data-testid="tool-result-envelope-error"
        className="rounded bg-destructive/10 p-2 text-xs text-destructive"
      >
        Malformed {toolName} envelope — expected JSON.
        <pre className="mt-1 whitespace-pre-wrap">{text}</pre>
      </div>
    );
  }
  const entries = Object.entries(parsed).map(
    ([k, v]): [string, string] => [k, typeof v === 'string' ? v : JSON.stringify(v)],
  );
  return (
    <div data-testid="tool-result-envelope">
      <KeyValueGrid entries={entries} />
    </div>
  );
}

export function PlatformCallResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (!isRecord(parsed)) {
    if (parsed == null && text.trim()) return <ProseBlock testId="tool-result-platform-call">{text}</ProseBlock>;
    return <Fallback text={text} toolName={toolName} />;
  }
  const resource = asString(parsed.resource);
  const method = asString(parsed.method);
  const headline =
    resource && method ? `${resource}.${method}` : resource ?? method ?? 'Platform call';

  const body = parsed.result ?? parsed.data ?? parsed.response;
  return (
    <div data-testid="tool-result-platform-call" className="space-y-2">
      <div className="text-sm font-medium">{headline}</div>
      {body !== undefined ? (
        <FlattenedJsonResult text={JSON.stringify(body)} toolName={toolName} />
      ) : (
        <FlattenedJsonResult text={text} toolName={toolName} />
      )}
    </div>
  );
}

export function CognitiveSpawnResult({ text }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (isRecord(parsed)) {
    const summary =
      asString(parsed.conclusion) ??
      asString(parsed.summary) ??
      asString(parsed.result) ??
      asString(parsed.message);
    if (summary) {
      return <ProseBlock testId="tool-result-cognitive">{summary}</ProseBlock>;
    }
  }
  return <ProseBlock testId="tool-result-cognitive">{text || 'Completed.'}</ProseBlock>;
}

export function AgentListResult({ text, toolName }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  const agents = asArray(isRecord(parsed) ? parsed.agents ?? parsed.personas ?? parsed.items : parsed);
  if (!agents) {
    if (parsed == null && text.trim()) return <ProseBlock testId="tool-result-agents">{text}</ProseBlock>;
    return <Fallback text={text} toolName={toolName} />;
  }
  const rows = agents.map((a, i) => {
    if (!isRecord(a)) return <span key={i}>{String(a)}</span>;
    return (
      <HitRow
        key={i}
        title={asString(a.name) ?? asString(a.persona_name) ?? `Agent ${i + 1}`}
        summary={asString(a.description)}
      />
    );
  });
  return (
    <div data-testid="tool-result-agents">
      <ClampedList items={rows} />
    </div>
  );
}

export function BashResult({ text }: ToolResultRendererProps) {
  const parsed = parseJsonPayload(text);
  if (isRecord(parsed)) {
    const stdout = asString(parsed.stdout) ?? '';
    const stderr = asString(parsed.stderr) ?? '';
    const combined = [stdout, stderr].filter(Boolean).join('\n');
    return <CodeBlock testId="tool-result-bash" text={combined || text} />;
  }
  return <CodeBlock testId="tool-result-bash" text={text} />;
}

type DiffLineKind = 'file' | 'hunk' | 'add' | 'del' | 'ctx';

function diffLineKind(line: string): DiffLineKind {
  if (line.startsWith('+++ ') || line.startsWith('--- ')) return 'file';
  if (line.startsWith('@@')) return 'hunk';
  if (line.startsWith('+')) return 'add';
  if (line.startsWith('-')) return 'del';
  return 'ctx';
}

const DIFF_LINE_CLASS: Record<DiffLineKind, string> = {
  file: 'text-muted-foreground',
  hunk: 'text-muted-foreground bg-muted',
  add: 'text-green-700 bg-green-500/10 dark:text-green-400',
  del: 'text-red-700 bg-red-500/10 dark:text-red-400',
  ctx: 'text-foreground/90',
};

export function DiffResult({ text, toolName }: ToolResultRendererProps) {
  if (parseJsonPayload(text) != null) return <Fallback text={text} toolName={toolName} />;

  const [summary, ...body] = text.replace(/\n$/, '').split('\n');
  return (
    <div data-testid="tool-result-diff" className="space-y-1.5">
      <p data-testid="tool-result-diff-summary" className="text-sm font-medium text-foreground/90">
        {summary}
      </p>
      {body.length > 0 && (
        <pre
          data-testid="tool-result-diff-body"
          className="max-h-64 overflow-auto rounded bg-muted/40 py-1 font-mono text-xs"
        >
          {body.map((line, i) => {
            const kind = diffLineKind(line);
            return (
              <div
                key={i}
                data-diff-line={kind}
                className={`whitespace-pre-wrap break-words px-2 ${DIFF_LINE_CLASS[kind]}`}
              >
                {line || '\u00a0'}
              </div>
            );
          })}
        </pre>
      )}
    </div>
  );
}
